import React, { useState, useEffect } from 'react';
import {
  Modal,
  Avatar,
  Button,
  Tag,
  Row,
  Col,
  Card,
  Divider,
  Input,
  Form,
  Select,
  Switch,
  message,
  Popover,
  List
} from 'antd';
import {
  EditOutlined,
  CloseOutlined,
  CheckOutlined,
  SaveOutlined,
  CheckCircleTwoTone,
  UserOutlined,
  PhoneOutlined,

  TeamOutlined,

} from '@ant-design/icons';
import './AccountDetailsModal.scss';
import { toast } from 'react-toastify';
import { DEFAULT_PROFILE_IMAGE } from '../../../../constants';
import { isDeepStrictEqual } from 'util';
import { fetchBusinessAccountList, updateBusinessAccountDetails } from '../../../../features/members/membersSlice';
import { useDispatch } from 'react-redux';
import { appDispatch } from '../../../../app/store';

const { Option } = Select;

interface UserData {
  id: number;
  name: string;
  first_name: string;
  last_name: string;
  display_name: string;
  username: string;
  email: string;
  email_pref: number;
  pager_number: string;
  pager_pref: number;
  profile_pic: string;
  user_type: string;
  source_of_reg_request: string;
  approval_status: string;
  network_roles: number[];
  roles: any;
  network_specialties: number[];
  practice_sites: Array<{
    name: string;
    phone_number_country_code: string;
    phone_number: string;
    position: string;
    country: { name: string; country_name_code: string };
    state: { name: string; country_name_code: string };
    city: { name: string; country_name_code: string };
    address_one: string;
    address_two: string;
    address_three: string;
  }>;
  medical_licenses: Array<{
    license_number: string;
    country: { name: string; country_name_code: string };
    issuing_state: { name: string; state_code: string };
    is_valid: boolean;
    validation_status: string;
    comment?: string;
  }>;
  contact_info: any;

  business_account: any;

  membership_plan: Array<{
    subscription_plan: {
      name: string;
      price: string;
      currency: string;
    };
  }>;
}

interface UserDetailsModalProps {
  fromMemberPage?: boolean;
  isVisible: boolean;
  user: UserData | null;
  selectedUserNetworks: any;
  onClose: () => void;
  onAccept: (user: UserData, isApprove: boolean, Reason?: string) => void;
  onReject: (user: UserData, isApprove: boolean, Reason?: string) => void;
  currentTab?: string;
  onEdit: (user: UserData) => void; // used for update/save
  onDelete: (user: UserData) => void;
  onVerifyLicense?: (user: UserData, licenseIndex: number) => void;
  onRejectLicense?: (user: UserData, licenseIndex: number, reason: string) => void;
  onEditLicense?: (user: UserData, licenseIndex: number, updatedLicense: UserData['medical_licenses'][number]) => void;
  onSaveLicense?: (user: UserData, licenseIndex: number, updatedLicense: UserData['medical_licenses'][number]) => void;
  isLicenseActionLoading?: boolean;
  isEditLicenseLoading?: boolean;
}


export const AccountDetailsModal: React.FC<UserDetailsModalProps> = ({
  fromMemberPage,
  isVisible,
  user,
  selectedUserNetworks,
  onClose,
  onAccept,
  onReject,
  currentTab,
  onEdit,
  onDelete,
  onVerifyLicense,
  onRejectLicense,
  onEditLicense,
  onSaveLicense,
  isLicenseActionLoading = false,
  isEditLicenseLoading = false
}) => {

  const [form] = Form.useForm();
  const dispatch = useDispatch<appDispatch>();

const [secondaryContact, setSecondaryContact] = useState(
  user?.contact_info?.secondary?.number 
);

  // console.log(user?.contact_info?.secondary?.number,"secondaryContact")
  // Inline license editing state
  const [editingLicenseIndex, setEditingLicenseIndex] = useState<number | null>(null);
  const [editedLicense, setEditedLicense] = useState<any | null>(null);

  const handleLicenseEditCancel = () => {
    setEditingLicenseIndex(null);
    setEditedLicense(null);
  };


  const [isEditingLeft, setIsEditingLeft] = useState(false);
  const [leftForm] = Form.useForm();

useEffect(() => {
  if (user) {
    setSecondaryContact(user?.contact_info?.secondary?.number ?? '');

    leftForm.setFieldsValue({
      name: user.name ?? '',
      secondary_phone: user?.contact_info?.secondary?.number ?? '',
    });
  } else {
    leftForm.resetFields();
  }
}, [user, isVisible]);

  const handleSaveLeftColumn = async () => {
    const values = await leftForm.validateFields();
    const secondaryPhone = values.secondary_phone?.trim();
    const primaryPhone = values.primary_phone?.trim() ?? null

    if (!user) return;

    // Start from existing data
    let updatedUser: UserData = { ...user };

    // Update name if changed
    if (values.name && values.name !== user.name) {
      updatedUser.name = values.name;
    }

    // Update secondary phone if provided
    if (secondaryPhone && secondaryPhone !== user.contact_info?.[1]?.number) {

      updatedUser.contact_info = [
        {
          ...user.contact_info?.[0],
          country_code: '',  // Primary record
          number: primaryPhone ? primaryPhone : '',
          number_status: "PRIMARY",
          pref: 1,
        },
        {
          ...user.contact_info?.[1],
          country_code: '',          // Secondary record
          number: secondaryPhone ? secondaryPhone : '',
          number_status: "SECONDARY",
          pref: 2,
        },
      ];

    } else if (!secondaryPhone) {
      // If secondary phone is empty, do NOT modify contact info
      delete updatedUser?.contact_info;
    }

    console.log("Final Updated User Payload:", updatedUser);

   dispatch(updateBusinessAccountDetails(updatedUser))
  .then((data) => {
    if (data.payload.isSuccess) {
      toast.success(data.payload.message);

      // ⭐ Update the local state with the new value
      const newSecondary =
        updatedUser?.contact_info?.[1]?.number || "";

      setSecondaryContact(newSecondary);

      // and update the form too
      leftForm.setFieldsValue({
        secondary_phone: newSecondary,
      });

      dispatch(fetchBusinessAccountList({ searchquery: "", pageNumber: 1 }));
      setIsEditingLeft(false);
    }
  });

  };


  return (
    <Modal
      open={isVisible}
      onCancel={() => {
        setIsEditingLeft(false);
        onClose();
      }}
      footer={null}
      width={'60%'}
      className="user-details-modal fixed-height-modal"
      centered={true}
      bodyStyle={{
        padding: '24px',
        background: 'var(--modal-background-color)',
        height: '650px',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {user && (
        <div className="user-details-content" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div className="modal-scroll-content" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '.75rem', minHeight: 0 }}>
          <Form form={leftForm} layout="vertical">

  <Row gutter={[24, 24]} className="profile-section">
    <Col sm={24} md={24} lg={24} xl={24}>
      <Card className="profile-card" bodyStyle={{ padding: '24px' }}>
        <div className="profile-header">
          <Avatar
            size={100}
            src={leftForm.getFieldValue('profile_pic') || user.profile_pic || DEFAULT_PROFILE_IMAGE}
            alt={user.display_name}
            className="profile-avatar"
          />

          <div className="profile-info" style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <>
                <h2 className="profile-name">
                  {!isEditingLeft ? (
                    <div className="info-item">
                      <span className="profile-name">{leftForm.getFieldValue("name") || user?.name}</span>
                    </div>
                  ) : (
                    <Form.Item name="name" style={{ margin: 0 }}>
                      <Input onChange={(e) => leftForm.setFieldsValue({ name: e.target.value })} />
                    </Form.Item>
                  )}
                </h2>
                <div className="profile-actions" style={{ display: 'flex', gap: '8px' }}>
                  <Button
onClick={() => {
  setIsEditingLeft(true);
  leftForm.setFieldsValue({
    secondary_phone: secondaryContact
  });
}}                 icon={!isEditingLeft ? <EditOutlined /> : null}
                    shape="circle"
                    size="middle"
                    className="edit-btn"
                    style={{ background: 'transparent' }}
                  />
                </div>
              </>
            </div>

            <Row gutter={[24, 12]}>
              <Col sm={24} md={24} lg={8} xl={8}>
                <div className="info-label">Username</div>
                <div className="info-value">{user?.username}</div>
              </Col>

                <Col sm={24} md={24} lg={8} xl={8}>
                <div className="info-label">ROLE</div>
                <div className="info-value">{user?.roles?.[0]}</div>
              </Col>
            </Row>
          </div>
        </div>
      </Card>
    </Col>
  </Row>

  {/* 2️⃣ BUSINESS + ROLE + CONTACT ROW */}
<Row gutter={[24, 24]} className="info-grid">

  {/* BUSINESS INFO COLUMN */}
  <Col sm={24} md={24} lg={12} xl={12}>
    <Card
      title={
        <div className="card-title">
          <PhoneOutlined className="card-icon contact-icon" />
          Business Account Information
        </div>
      }
      size="small"
      className="info-card"
      headStyle={{ background: 'var(--country-dropdown-hover)', color: 'white' }}
      bodyStyle={{ padding: '20px' }}
    >
      <Row gutter={[16, 12]}>
        <Col span={24}>
          <div className="info-item">
            <div className="info-label">Business Name</div>
            <div className="info-value">{user?.business_account?.business_name ?? "N/A"}</div>
          </div>
        </Col>

        <Col span={24}>
          <div className="info-item">
            <div className="info-label">Tax ID</div>
            <div className="info-value">{user?.business_account?.tax_id ?? "N/A"}</div>
          </div>
        </Col>
      </Row>
    </Card>
  </Col>

  {/* CONTACT INFO COLUMN */}
  <Col sm={24} md={24} lg={12} xl={12}>
    <Card
      title={
        <div className="card-title">
          <PhoneOutlined className="card-icon contact-icon" />
          Contact Information
        </div>
      }
      size="small"
      className="info-card"
      headStyle={{ background: 'var(--country-dropdown-hover)', color: 'white' }}
      bodyStyle={{ padding: '20px' }}
    >
      <Row gutter={[16, 12]}>
        <Col span={24}>
          <div className="info-item">
            <div className="info-label">Email</div>
            <div className="info-value">{user?.email}</div>
          </div>
        </Col>

        <Col span={24}>
          <div className="info-item">
            <div className="info-label">Primary Phone</div>
            <div className="info-value">
              {user.contact_info?.primary?.number === null ||''? "N/A":user.contact_info?.primary?.number}
            </div>
          </div>
        </Col>

       <Col span={24}>
  <div className="info-item">
    <div className="info-label">Secondary Phone</div>

    {isEditingLeft ? (
      <Form.Item name="secondary_phone" style={{ margin: 0 }}>
        <Input
          placeholder="Enter secondary number"
        />
      </Form.Item>
    ) : (
      <div className="info-value">
        {leftForm.getFieldValue("secondary_phone") || secondaryContact || "N/A"}
      </div>
    )}
  </div>
</Col>
      </Row>
    </Card>
  </Col>

</Row>
</Form>
          </div>

          {/* Footer Actions */}
          {isEditingLeft && (
            <div className="modal-footer">
              <Button danger size="large" onClick={() => setIsEditingLeft(false)} className="reject-btn">
                Cancel
              </Button>
              <Button type="primary" size="large" onClick={handleSaveLeftColumn} className="accept-btn">
                Update
              </Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};